import {
  OnboardingBaseTask,
  OnboardingProTask,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { i18next } from '@frontend/i18n';
import { HOTSPOT_TARGET, type OnboardingFlow } from '@frontend/product-onboarding';
import { BaseProductTaskContent } from './components/base-product-task-content';
import { BaseGetStartedContent, ProGetStartedContent } from './components/get-started-content';
import { HotspotTooltipContent } from './components/hotspot-tooltip-content';
import { ProProductTaskContent } from './components/pro-product-task-content';
import {
  CI_BASE_ONBOARDING,
  CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS,
  CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS,
  CI_PRO_ONBOARDING,
} from './constant';
import {
  CI_BASE_ONBOARDING_EVENTS,
  CI_BASE_ONBOARDING_TRACKING_IDS,
  CI_PRO_ONBOARDING_EVENTS,
  CI_PRO_ONBOARDING_TRACKING_IDS,
} from './tracking';

interface BuildCIBaseOnboardingFlowArgs {
  canAssignUserRoles: boolean;
}

export function buildCIBaseOnboardingFlow({ canAssignUserRoles }: BuildCIBaseOnboardingFlowArgs): OnboardingFlow {
  const tasks: OnboardingFlow['tasks'] = [
    {
      id: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      label: i18next.t('Uncover enhanced Call Insights', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Quickly see the call summary and transcript', { ns: 'call-intel-onboarding' }),
      tracking: {
        completedEvent: CI_BASE_ONBOARDING_EVENTS.taskCompleted.viewCallInsights,
        navigationTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewCallInsights,
      },
    },
    {
      id: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      label: i18next.t('Access call summaries in the patient profile', { ns: 'call-intel-onboarding' }),
      description: i18next.t('View previous conversations in the patient Overview and History', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_BASE_ONBOARDING_EVENTS.taskCompleted.viewPatientContext,
        navigationTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewPatientContext,
      },
    },
  ];

  const hotspots: OnboardingFlow['hotspots'] = [
    // Explore Call History
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALLS_NAVIGATION_MENU,
      target: HOTSPOT_TARGET.CALLS_NAVIGATION_MENU,
      group: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.callsNavigation,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.callsNavigation,
      },
    },
    // View Call Insights: open the panel, see summary & transcript
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_HISTORY_CALL_INSIGHT_BUTTON,
      target: HOTSPOT_TARGET.CALL_HISTORY_CALL_INSIGHT_BUTTON,
      group: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.callInsightButton,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.callInsightButton,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      group: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      completes: [
        CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_HISTORY_CALL_INSIGHT_BUTTON,
        CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALLS_NAVIGATION_MENU,
      ],
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.transcriptTab,
      },
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.transcriptTab,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.transcriptTab,
      },
    },

    // View Patient Context: call intelligence inside the patient profile
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR,
      target: HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.patientAvatar,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.patientAvatar,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_LAST_CALL_CARD,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      completes: [CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR],
      completeGroups: [OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT],
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.lastCallCard,
      },
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.lastCallCard,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.lastCallCard,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.viewHistoryButton,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.viewHistoryButton,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_HISTORY_TAB_CALL,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_HISTORY_TAB_CALL,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      kind: 'info',
      dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.patientHistoryCall,
      },
    },
  ];

  if (canAssignUserRoles) {
    tasks.push({
      id: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
      label: i18next.t('Loop in your team', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Assign the Data Analyst role to your team members in User Management', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_BASE_ONBOARDING_EVENTS.taskCompleted.assignUserRoles,
        navigationTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.taskNavigationClick.assignUserRoles,
      },
    });

    hotspots.push(
      {
        key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.TOP_BAR_SETTINGS_ICON,
        target: HOTSPOT_TARGET.TOP_BAR_SETTINGS_ICON,
        group: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
        dependencies: {
          all: [
            CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
            CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
          ],
        },
        tracking: {
          seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.settingsIcon,
          interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.settingsIcon,
        },
      },
      {
        key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        target: HOTSPOT_TARGET.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        group: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
        dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.TOP_BAR_SETTINGS_ICON,
        tracking: {
          seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.userManagementNav,
          interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.userManagementNav,
        },
      },
      {
        key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
        target: HOTSPOT_TARGET.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
        group: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
        kind: 'info',
        dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        tooltip: {
          Content: HotspotTooltipContent,
          trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.userTableAction,
        },
      }
    );
  }

  return {
    id: CI_BASE_ONBOARDING.id,
    priority: CI_BASE_ONBOARDING.priority,
    NavContent: BaseGetStartedContent,
    TaskContent: BaseProductTaskContent,
    tasks,
    hotspots,
    tracking: {
      guideTriggerTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.getStartedNavClick,
      checklistSeenEvent: CI_BASE_ONBOARDING_EVENTS.checklistSeen,
    },
  };
}

interface BuildCIProOnboardingFlowArgs {
  canAssignUserRoles: boolean;
  canEditAiOutput: boolean;
}

export function buildCIProOnboardingFlow({
  canAssignUserRoles,
  canEditAiOutput,
}: BuildCIProOnboardingFlowArgs): OnboardingFlow {
  const lastReviewHotspotKey = canEditAiOutput
    ? CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_MAKE_CORRECTION_BUTTON
    : CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_CALL_BREAKDOWN;

  const makeCorrectionHotspots: OnboardingFlow['hotspots'] = canEditAiOutput
    ? [
        {
          key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_MAKE_CORRECTION_BUTTON,
          target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_MAKE_CORRECTION_BUTTON,
          group: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
          dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_CALL_BREAKDOWN,
          tracking: {
            seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.makeCorrectionButton,
            interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.makeCorrectionButton,
          },
        },
        {
          key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_EDIT_AI_VIEW_LABEL,
          target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_EDIT_AI_VIEW_LABEL,
          group: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
          kind: 'info',
          dependencies: lastReviewHotspotKey,
          tooltip: {
            Content: HotspotTooltipContent,
            trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.editAiView,
          },
        },
      ]
    : [];

  const tasks: OnboardingFlow['tasks'] = [
    {
      id: OnboardingProTask.PRO_TASK_OVERVIEW_INSIGHTS,
      label: i18next.t('Understand your insights', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Click any info icon on the page to learn how it’s defined and why it matters', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.overviewInsights,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.overviewInsights,
      },
    },
    {
      id: OnboardingProTask.PRO_TASK_OVERVIEW_CHART_DRILLDOWN,
      label: i18next.t('Click into any data point', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Dig deeper and uncover the list of calls for every data point', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.overviewChartDrilldown,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.overviewChartDrilldown,
      },
    },
    {
      id: OnboardingProTask.PRO_TASK_VIEW_CALL_INSIGHTS,
      label: i18next.t('Uncover enhanced Call Insights', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Quickly see the call summary and transcript', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.viewCallInsights,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewCallInsights,
      },
    },
    {
      id: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
      label: i18next.t('Dive into the Conversation Analysis', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Review each call’s AI-powered insights in the Call Breakdown', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.reviewConversationAnalysis,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.reviewConversationAnalysis,
      },
    },
    {
      id: OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS,
      label: i18next.t('Catch up on Follow-up Tasks', { ns: 'call-intel-onboarding' }),
      description: i18next.t('See follow-up tasks automatically created for each phone call', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.viewFollowUpTasks,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewFollowUpTasks,
      },
    },
    {
      id: OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
      label: i18next.t('Access patient-level data', { ns: 'call-intel-onboarding' }),
      description: i18next.t('View past Call Intelligence insights in the patient profile', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.viewPatientContext,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewPatientContext,
      },
    },
  ];

  const hotspots: OnboardingFlow['hotspots'] = [
    // Task 1: Understand your insights
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
      target: HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
      group: OnboardingProTask.PRO_TASK_OVERVIEW_INSIGHTS,
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.activeOpportunitiesInfoIcon,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.activeOpportunitiesInfoIcon,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON_TOOLTIP_POPOVER,
      target: HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON_TOOLTIP_POPOVER,
      group: OnboardingProTask.PRO_TASK_OVERVIEW_INSIGHTS,
      kind: 'info',
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.activeOpportunities,
      },
    },

    // Task 2: Explore drilldown calls
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT,
      target: HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT,
      group: OnboardingProTask.PRO_TASK_OVERVIEW_CHART_DRILLDOWN,
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.unhappyCallsChartSegment,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.unhappyCallsChartSegment,
      },
    },

    // Task 3: View Call Insights , see summary & transcript
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_BUTTON,
      target: HOTSPOT_TARGET.CALL_INTEL_CALL_INSIGHT_BUTTON,
      group: OnboardingProTask.PRO_TASK_VIEW_CALL_INSIGHTS,
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.callInsightButton,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.callInsightButton,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      group: OnboardingProTask.PRO_TASK_VIEW_CALL_INSIGHTS,
      dependencies: {
        any: [
          CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_BUTTON,
          CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
        ],
      },
      completes: [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_BUTTON],
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.transcriptTab,
      },
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.transcriptTab,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.transcriptTab,
      },
    },

    // Task 4: Conversation Analysis
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_CALL_BREAKDOWN,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_CALL_BREAKDOWN,
      group: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      completeGroups: [OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS],
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.callBreakdownAccordion,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.callBreakdownAccordion,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_SCHEDULING_OUTCOME_LABEL,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_SCHEDULING_OUTCOME_LABEL,
      group: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
      kind: 'info',
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_CALL_BREAKDOWN,
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.classifications,
      },
    },
    ...makeCorrectionHotspots,
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_FEEDBACK_BUTTON,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_FEEDBACK_BUTTON,
      group: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
      dependencies: lastReviewHotspotKey,
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.callLevelFeedback,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.callLevelFeedback,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL,
      group: OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
      kind: 'info',
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_FEEDBACK_BUTTON,
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.feedbackModal,
      },
    },

    // Task 5: Follow-up Tasks
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASKS_TAB,
      target: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_TASKS_TAB,
      group: OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS,
      dependencies: {
        all: [
          CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
          CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT,
        ],
      },
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.followUpTasksTab,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.followUpTasksTab,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON,
      target: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON,
      group: OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS,
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASKS_TAB,
      completes: [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON],
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.schedulingTasksListButton,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.schedulingTasksListButton,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON,
      target: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON,
      group: OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS,
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASKS_TAB,
      completes: [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON],
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.allFollowUpsListButtonFallback,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.allFollowUpsListButtonFallback,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASK_ROW,
      target: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_TASK_ROW,
      group: OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS,
      kind: 'info',
      dependencies: {
        any: [
          CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON,
          CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON,
        ],
      },
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.followUpTask,
      },
    },

    // Task 6: Patient-level Data
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR,
      target: HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR,
      group: OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.patientAvatar,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.patientAvatar,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_LAST_CALL_CARD,
      group: OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
      completes: [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR],
      completeGroups: [OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT],
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.lastCallCard,
      },
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.lastCallCard,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.lastCallCard,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      group: OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
      tracking: {
        seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.viewHistoryButton,
        interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.viewHistoryButton,
      },
    },
    {
      key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_HISTORY_TAB_CALL,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_HISTORY_TAB_CALL,
      group: OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
      kind: 'info',
      dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.patientHistoryCall,
      },
    },
  ];

  if (canAssignUserRoles) {
    tasks.push({
      id: OnboardingProTask.PRO_TASK_ASSIGN_USER_ROLES,
      label: i18next.t('Loop in your team', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Assign the Data Analyst role to your team members in User Management', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_PRO_ONBOARDING_EVENTS.taskCompleted.assignUserRoles,
        navigationTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.taskNavigationClick.assignUserRoles,
      },
    });

    hotspots.push(
      {
        key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.TOP_BAR_SETTINGS_ICON,
        target: HOTSPOT_TARGET.TOP_BAR_SETTINGS_ICON,
        group: OnboardingProTask.PRO_TASK_ASSIGN_USER_ROLES,
        dependencies: {
          all: [
            CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
            CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT,
            CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_BUTTON,
            CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_CALL_BREAKDOWN,
            CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASKS_TAB,
            CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
          ],
        },
        tracking: {
          seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.settingsIcon,
          interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.settingsIcon,
        },
      },
      {
        key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        target: HOTSPOT_TARGET.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        group: OnboardingProTask.PRO_TASK_ASSIGN_USER_ROLES,
        dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.TOP_BAR_SETTINGS_ICON,
        tracking: {
          seenEvent: CI_PRO_ONBOARDING_EVENTS.hotspotSeen.userManagementNav,
          interactionTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.hotspotInteraction.userManagementNav,
        },
      },
      {
        key: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
        target: HOTSPOT_TARGET.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
        group: OnboardingProTask.PRO_TASK_ASSIGN_USER_ROLES,
        kind: 'info',
        dependencies: CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        tooltip: {
          Content: HotspotTooltipContent,
          trackingSeenEvent: CI_PRO_ONBOARDING_EVENTS.tooltipSeen.userTableAction,
        },
      }
    );
  }

  return {
    id: CI_PRO_ONBOARDING.id,
    priority: CI_PRO_ONBOARDING.priority,
    NavContent: ProGetStartedContent,
    TaskContent: ProProductTaskContent,
    tasks,
    hotspots,
    tracking: {
      guideTriggerTrackingId: CI_PRO_ONBOARDING_TRACKING_IDS.getStartedNavClick,
      checklistSeenEvent: CI_PRO_ONBOARDING_EVENTS.checklistSeen,
    },
  };
}
